import React, {useRef, useState, useMemo} from "react";

import { A11y, Ghost } from "@churchofjesuschrist/eden-buttons";
import { Select } from "@churchofjesuschrist/eden-form-parts";
import {
  Chapel,
  ChevronRight,
  LocationTarget,
  World,
  Clear
} from "@churchofjesuschrist/eden-icons";
import { Row } from "@churchofjesuschrist/eden-row";
import { Stack } from "@churchofjesuschrist/eden-stack";
import {
  blue20,
  accentColorPrimary,
  white,
  backgroundColorAlt as flyoutGrey,
  backgroundColorAlt as columnGrey,
  borderColorTertiary as openGrey,
  spacing4,
  spacing8,
  spacing16,
} from "@churchofjesuschrist/eden-style-constants";
import styled from "styled-components";

import FilterTextInput from "../../generic/filter-text-input/filter-text-input";
import useOutsideClick from "../../generic/hooks/use-outside-click/use-outside-click";
import NavigationContext from "../../shared/navigation-link/navigation-context";
import manageStringsHelper from "../../utils/manage-strings-helper";
import withStrings from "../../utils/strings/with-strings";

const headerFooterHeight = 90;
const buttonWidth = 24; //width of the chevron
//this is also the width of this navigation element in actual application
const flyoutWidth = 320;
const ColumnChild = styled.div`
  padding: 0 ${spacing16};
`;
const RowHolder = styled.div`
  padding: ${(props) => (props.$open ? spacing4 : "0")} ${spacing16};
  position: relative;
  width: 100%;
  cursor: ${(props) => (props.$active ? "pointer" : "auto")};
  background: ${(props) => (props.$open ? openGrey : props.$isMobile ? white : columnGrey)};
`;
const NameDiv = styled.div`
  flex:1;
`;
const RightDiv = styled(A11y)`
  width: ${buttonWidth}px;
`;

const ComponentHolder = styled.div`
  position: relative;
  background-color:${({$isMobile}) => $isMobile ? white : columnGrey};
  padding: ${spacing8} 0;
`;
const normalHeightStyle = `max-height: calc(100vh - ${headerFooterHeight}px)`;


const FlyOut = styled.div`
  position: absolute;
  background-color: ${flyoutGrey};
  padding: ${spacing16};
  top: 0;
  right: ${({$isMobile, $deferredToParent}) => $deferredToParent ? "auto" : $isMobile ? "0" : `-${flyoutWidth}px`};
  ${({$isMobile}) => $isMobile ? "" : normalHeightStyle};
  width:${({$isMobile}) => $isMobile ? "100%" : `${flyoutWidth}px` };
  z-index: 2000;
  ${({$isMobile}) => $isMobile ? "" : "overflow-y: auto;"}
`;
const ListHeader = styled.div`
display:flex;
flex-direction: ${({$showFilter}) => $showFilter ? "row" : "row-reverse"};
${({$isMobile, $showFilter}) => $isMobile && !$showFilter ? "position:absolute; right:0;" : ""}
`;


const FilterHolder = styled.div`
flex:1;
`;
const CloseFlyoutButton = styled(A11y)`
padding:${spacing8} ${spacing16};
`
const VerticalCenteredRow = styled(Row)`
  align-items: center;
  flex-wrap: nowrap;
  display:flex;
`;
const ItemA11y = styled(A11y)`
  padding-left: ${spacing8};
  border-left: ${(props) =>
  props.$active ? `solid ${spacing4} ${blue20}` : "none"};
`;
const IconHolder = styled(A11y)`
  min-width: 24px;
`;

const stringGroups = [
  {
    name: "shared",
    keys: [
      "selectArea",
      "selectRegion",
      "selectProgram",
      "selectCoordinator",
      "addYear",
    ],
  },
];

const NewLevelFlyout = ({level, active, items, onClicked, isMobile, onLevelFlyoutOpened, onOutsideClick}) => {
  let useActive = active;
  if (useActive === null) {
    useActive = {};
  }

  const flyoutId = `${level}-flyout`;
  const showFilter = items && items.length >= 10;

  const [filter, setFilter] = useState("");
  const filteredItems = useMemo(() => !showFilter ? items :
    items.filter(item => item.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0),
  [items, showFilter, filter]);
  const [, setFirstClick] = useState(false);
  const ref = useRef();

  useOutsideClick(ref, (e) => {
    setFirstClick(cur => {
      if (cur) {
        onOutsideClick();
      }
      return true;
    })
  });

  const result = (
    <FlyOut ref={ref} data-id={flyoutId} $isMobile={isMobile} $deferredToParent={typeof onLevelFlyoutOpened === "function"}>
      <Stack>
        <ListHeader $showFilter={showFilter} $isMobile={isMobile}>
          {showFilter && <FilterHolder><FilterTextInput
            onChange={setFilter}
          /></FilterHolder>}
          {isMobile && <CloseFlyoutButton onClick={onOutsideClick}><Clear size="20px"/></CloseFlyoutButton>}
        </ListHeader>


        {filteredItems.map((item) => (
          <ItemA11y
            data-id={`${flyoutId}-${item.id}`}
            $active={item.id === useActive.id}
            key={item.id}
            onClick={() => {
              onClicked(item);
            }}
          >
            {item.name}
          </ItemA11y>
        ))}
      </Stack>
    </FlyOut>);
  return result;
}

class ProgramTreeNavigation extends React.Component {
  static contextType = NavigationContext;
  state = { activeFlyout: null, flyoutFilter: null };

  programTreeNavRef = React.createRef();
  windowClickFunction = (e) => {
    if (
      !this.programTreeNavRef.current.contains(e.target) &&
      this.state.activeFlyout
    ) {
      this.setState({ activeFlyout: null, flyoutFilter: null });
    }
  };
  closeFlyout = () => {
    const {onLevelFlyoutOpened} = this.props;
    this.setState({ activeFlyout: null, flyoutFilter: null });
    if (typeof(onLevelFlyoutOpened) === "function") {
      onLevelFlyoutOpened(null);
    }
  }
  componentDidMount = () => {
    window.addEventListener("click", this.windowClickFunction);
  };
  componentWillUnmount = () => {
    window.removeEventListener("click", this.windowClickFunction);
  };
  levelClicked = (lvl) => {
    if (this.state.activeFlyout !== lvl) {
      this.setState({ activeFlyout: lvl, flyoutFilter: null });
      const {onLevelFlyoutOpened} = this.props;
      if (typeof(onLevelFlyoutOpened) === "function") {
        const {
          area, areas,
          region, regions,
          coordinator, coordinators,
          program, programs
        } = this.context;
        if (lvl === "area") {
          onLevelFlyoutOpened(this.LevelFlyout("areas", areas, undefined, area,this.onAreaClicked));
        }
        if (lvl === "region") {
          onLevelFlyoutOpened(this.LevelFlyout("regions", regions, undefined, region,this.onRegionClicked));
        }
        if (lvl === "coordinator") {
          onLevelFlyoutOpened(this.LevelFlyout("coordinators", coordinators, undefined, coordinator,this.onCoordinatorClicked));
        }
        if (lvl === "program") {
          onLevelFlyoutOpened(this.LevelFlyout("programs", programs, undefined, program,this.onProgramClicked));
        }
      }
    }
  };
  yearSelectChosen = (yearId, onYearClicked) => {
    const intId = parseInt(yearId);
    if (isNaN(intId)) {
      return;
    }
    if (intId === -1) {
      //this is an add year action
      onYearClicked({ id: -1, name: "add year" });
      return;
    }
    const { years } = this.context;
    let match = years.find((y) => y.id === parseInt(yearId));
    onYearClicked(match);
  };
  termSelectChosen = (termId, onTermClicked) => {
    if (termId === undefined) {
      return;
    }
    const { terms } = this.context;
    const match = terms.find((t) => t.id === parseInt(termId));
    onTermClicked(match);
  };
  onFlyoutFilterChange = (value) => {
    const filter = value;
    this.setState({ flyoutFilter: filter });
  };

  LevelFlyout = (level, items, filter, active = {}, onClicked) => {
    let useActive = active;
    if (useActive === undefined) {
      useActive = {};
    }
    const { isMobile = false, onLevelFlyoutOpened } = this.props;
    return <NewLevelFlyout level={level}
      active={active}
      items={items}
      onClicked={onClicked}
      isMobile={isMobile}
      onLevelFlyoutOpened={onLevelFlyoutOpened}
      onOutsideClick={this.closeFlyout}/>
  };

  onAreaClicked = (a) => {
    const {onAreaClicked:clicked} = this.props;
    clicked(a);
    this.closeFlyout();
  }
  onRegionClicked = (a) => {
    const {onRegionClicked:clicked} = this.props;
    clicked(a);
    this.closeFlyout();
  }
  onCoordinatorClicked = (a) => {
    const {onCoordinatorClicked:clicked} = this.props;
    clicked(a);
    this.closeFlyout();
  }
  onProgramClicked = (a) => {
    const {onProgramClicked:clicked} = this.props;
    clicked(a);
    this.closeFlyout();
  }

  render() {
    let {
      children,
      onYearClicked = () => {},
      onTermClicked = () => {},
      strings: originalStrings,
      canAddYear = false,
      isMobile = false,
      onLevelFlyoutOpened
    } = this.props;


    let {
      areas = [],
      area,
      regions = [],
      region,
      coordinators = [],
      coordinator,
      programs = [],
      program,
      years = [],
      year,
      terms = [],
      term
    } = this.context || {};
    if (areas === null) {
      areas = [];
    }
    if (regions === null) {
      regions = [];
    }
    if (coordinators === null) {
      coordinators = [];
    }
    if (programs === null) {
      programs = [];
    }
    if (years === null) {
      years = [];
    }
    if (terms === null) {
      terms = [];
    }

    years = years.sort((a, b) => (a.name < b.name ? 1 : -1));
    const { activeFlyout, flyoutFilter } = this.state;
    const strings = manageStringsHelper(originalStrings, stringGroups);
    const ownsFlyout = typeof(onLevelFlyoutOpened) !== "function";
    const showAreas = activeFlyout === "area" && areas.length > 0;
    const showRegions =  activeFlyout === "region" && regions.length > 0;
    const showCoordinators =  activeFlyout === "coordinator" && coordinators.length > 0;
    const showPrograms = activeFlyout === "program" && programs.length > 0;
    const iconSize = "24px";
    return (
      <ComponentHolder
        ref={this.programTreeNavRef}
        data-id="program-tree-navigation"
        $isMobile={isMobile}
      >
        <Stack>
          <ColumnChild>{children}</ColumnChild>
          {/*Area */}
          <RowHolder
            $isMobile={isMobile}
            $open={showAreas}
            $active={Boolean(areas)}
            data-id="area-row"
            onClick={(e) => {
              e.preventDefault();
              this.levelClicked("area");
            }}
          >
            <VerticalCenteredRow gapSize="8">
              <IconHolder>
                <World size={iconSize} />
              </IconHolder>
              <NameDiv>{area ? area.name : strings.selectArea}</NameDiv>

              <RightDiv>
                <ChevronRight size={iconSize} color={accentColorPrimary} />
              </RightDiv>
            </VerticalCenteredRow>
          </RowHolder>
          {/*Region*/}
          {regions.length > 0 ? (
            <RowHolder
              $isMobile={isMobile}
              $open={showRegions}
              data-id="region-row"
              $active={Boolean(regions)}
              onClick={() => {
                this.levelClicked("region");
              }}
            >
              <VerticalCenteredRow gapSize="8">
                <IconHolder>
                  <LocationTarget size={iconSize} />
                </IconHolder>
                <NameDiv>{region ? region.name : strings.selectRegion}</NameDiv>

                <RightDiv>
                  <ChevronRight size={iconSize} color={accentColorPrimary} />
                </RightDiv>
              </VerticalCenteredRow>
            </RowHolder>
          ) : undefined}
          {/*Coordinator */}
          {coordinators.length > 0 ? (
            <RowHolder
              $isMobile={isMobile}
              $open={showCoordinators}
              data-id="coordinator-row"
              $active={Boolean(regions)}
              onClick={() => {
                this.levelClicked("coordinator");
              }}
            >
              <VerticalCenteredRow gapSize="8">
                <IconHolder>
                  <LocationTarget size={iconSize} />
                </IconHolder>
                <NameDiv>
                  {coordinator ? coordinator.name : strings.selectCoordinator}
                </NameDiv>

                <RightDiv>
                  <ChevronRight size={iconSize} color={accentColorPrimary} />
                </RightDiv>
              </VerticalCenteredRow>
            </RowHolder>
          ) : undefined}
          {/*Program*/}
          {programs.length > 0 ? (
            <RowHolder
              $isMobile={isMobile}
              $open={showPrograms}
              data-id="program-row"
              $active={Boolean(programs)}
              onClick={() => {
                this.levelClicked("program");
              }}
            >
              <VerticalCenteredRow gapSize="8">
                <IconHolder>
                  <Chapel size={iconSize} />
                </IconHolder>
                <NameDiv>
                  {program ? program.name : strings.selectProgram}
                </NameDiv>

                <RightDiv>
                  <ChevronRight size={iconSize} color={accentColorPrimary} />
                </RightDiv>
              </VerticalCenteredRow>
            </RowHolder>
          ) : undefined}
          {years.length > 0 ? (
            <ColumnChild>
              <VerticalCenteredRow>
                <Select
                  data-id="year-select"
                  value={year ? year.id.toString() : ""}
                  onChange={(e) =>
                    this.yearSelectChosen(e.target.value, onYearClicked)
                  }
                >
                  {!year ? <option value="" /> : undefined}
                  {canAddYear ? (
                    <option value={"-1"}>{strings.addYear}</option>
                  ) : undefined}

                  {years.map((year) => (
                    <option key={year.id} value={year.id.toString()}>
                      {year.name}
                    </option>
                  ))}
                </Select>
                {terms.length > 0 ? (
                  <Select
                    data-id="terms-select"
                    value={term ? term.id.toString() : ""}
                    onChange={(e) =>
                      this.termSelectChosen(e.target.value, onTermClicked)
                    }
                  >
                    {!term ? <option value={"0"} /> : undefined}
                    {terms.map((term) => (
                      <option key={term.id} value={term.id.toString()}>
                        {term.name}
                      </option>
                    ))}
                  </Select>
                ) : undefined}
              </VerticalCenteredRow>
            </ColumnChild>
          ) : program ? (
            <ColumnChild>
              <Ghost onClick={() => this.yearSelectChosen("-1", onYearClicked)}>
                {strings.addYear}
              </Ghost>
            </ColumnChild>
          ) : undefined}
        </Stack>
        {showAreas && ownsFlyout
          ? this.LevelFlyout("areas", areas, flyoutFilter, area, this.onAreaClicked)
          : undefined}
        {showRegions && ownsFlyout
          ? this.LevelFlyout(
            "regions",
            regions,
            flyoutFilter,
            region,
            this.onRegionClicked
          )
          : undefined}
        {showCoordinators && ownsFlyout
          ? this.LevelFlyout(
            "coordinators",
            coordinators,
            flyoutFilter,
            coordinator,
            this.onCoordinatorClicked
          )
          : undefined}
        {showPrograms && ownsFlyout
          ? this.LevelFlyout(
            "programs",
            programs,
            flyoutFilter,
            program,
            this.onProgramClicked
          )
          : undefined}
      </ComponentHolder>
    );
  }
}

export default withStrings(ProgramTreeNavigation);
